import type { ReplaySpeed, ReplayState } from '../hooks/use-session-replay.js';
import { Button } from './button.js';

const SPEEDS: ReplaySpeed[] = [0.5, 1, 2, 4];

interface ReplayControlsProps {
  state: ReplayState;
  speed: ReplaySpeed;
  progress: number;
  currentIndex: number;
  totalEvents: number;
  onPlay: () => void;
  onPause: () => void;
  onSetSpeed: (speed: ReplaySpeed) => void;
  onSeek: (index: number) => void;
  onReset: () => void;
}

export function ReplayControls({
  state,
  speed,
  progress,
  currentIndex,
  totalEvents,
  onPlay,
  onPause,
  onSetSpeed,
  onSeek,
  onReset,
}: ReplayControlsProps) {
  const isPlaying = state === 'playing';
  const isFinished = state === 'finished';

  return (
    <div className="flex flex-col gap-2" data-testid="replay-controls">
      {/* Timeline scrubber */}
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={Math.max(0, totalEvents - 1)}
          value={currentIndex}
          onChange={(e) => onSeek(parseInt(e.target.value, 10))}
          className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full"
          style={{ backgroundColor: 'var(--color-surface-3)', accentColor: 'var(--color-brand)' }}
          data-testid="replay-scrubber"
        />
        <span className="min-w-[4rem] text-right text-xs tabular-nums" style={{ color: 'var(--color-text-3)' }} data-testid="replay-progress">
          {currentIndex + 1} / {totalEvents}
        </span>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-3">
        {/* Play/Pause button */}
        {isPlaying ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onPause}
            data-testid="replay-pause"
          >
            Pause
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={onPlay}
            data-testid="replay-play"
          >
            {isFinished ? 'Replay' : state === 'idle' ? 'Play' : 'Resume'}
          </Button>
        )}

        {/* Reset */}
        {state !== 'idle' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            data-testid="replay-reset"
          >
            Reset
          </Button>
        )}

        {/* Speed selector */}
        <div className="ml-auto flex items-center gap-1">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSetSpeed(s)}
              className="rounded px-2 py-1 text-xs font-medium"
              style={speed === s
                ? { backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-brand)' }
                : { color: 'var(--color-text-3)' }
              }
              data-testid={`replay-speed-${s}`}
            >
              {s}x
            </button>
          ))}
        </div>

        {/* Progress bar visual */}
        <div className="hidden sm:block">
          <div className="h-1 w-24 overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-surface-3)' }}>
            <div
              className="h-full rounded-full transition-all duration-200"
              style={{ backgroundColor: 'var(--color-brand)', width: `${progress * 100}%` }}
              data-testid="replay-progress-bar"
            />
          </div>
        </div>
      </div>
      {/* Keyboard shortcut hint — hidden on touch devices */}
      <div
        className="hidden text-center"
        style={{
          color: 'var(--color-text-3)',
          fontSize: '10px',
        }}
        data-testid="replay-shortcuts-hint"
      >
        Space play/pause &middot; &larr;&rarr; step &middot; [ ] speed &middot; R reset &middot; Esc exit
      </div>
      <style>{`@media (hover: hover) { [data-testid="replay-shortcuts-hint"] { display: block !important; } }`}</style>
    </div>
  );
}
